import { Link, useParams, useRouter } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle,
  Download,
  FileText,
  Printer,
  Save,
  Send,
  ShieldAlert,
  Trash,
  Upload,
  XCircle,
} from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { AttachmentSection } from "@/components/shared/AttachmentSection";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { EnrichedSubLineItem } from "@/hooks/use-pay-applications";
import {
  useApproveSubPayApp,
  useDeleteSubPayApp,
  useLienWaivers,
  useRejectSubPayApp,
  useReturnSubPayAppForRevision,
  useSubmitSubPayApp,
  useSubPayApp,
  useUpdateSubPayAppLineItems,
  useUploadLienWaiver,
} from "@/hooks/use-pay-applications";
import { useSession } from "@/lib/auth-client";

function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "--";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "--";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatDate(date: string | null | undefined): string {
  if (!date) return "--";
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusVariant(
  status: string | null,
): "success" | "warning" | "secondary" | "destructive" | "default" {
  switch (status) {
    case "approved":
    case "paid":
      return "success";
    case "draft":
    case "submitted":
      return "warning";
    case "under_review":
      return "secondary";
    case "rejected":
    case "returned_for_revision":
      return "destructive";
    default:
      return "default";
  }
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "--";
  if (status === "returned_for_revision") return "Returned for Revision";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

interface EditedValues {
  completedThisPeriod: string;
  storedMaterials: string;
}

export function SubcontractPayAppDetailPage() {
  const { id } = useParams({ strict: false });
  const payAppId = id ?? "";
  const router = useRouter();
  const { data: payApp, isLoading, isError, error, refetch } = useSubPayApp(payAppId);
  const submitMutation = useSubmitSubPayApp();
  const approveMutation = useApproveSubPayApp();
  const rejectMutation = useRejectSubPayApp();
  const returnMutation = useReturnSubPayAppForRevision();
  const deleteMutation = useDeleteSubPayApp();
  const updateLineItemsMutation = useUpdateSubPayAppLineItems(payAppId);
  const { data: session } = useSession();
  const { data: lienWaivers } = useLienWaivers(payAppId);
  const uploadLienWaiverMutation = useUploadLienWaiver(payAppId);
  const lienWaiverFileRef = useRef<HTMLInputElement>(null);

  const [editedItems, setEditedItems] = useState<Record<string, EditedValues>>({});
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load pay application"}
        onRetry={() => refetch()}
      />
    );
  }

  if (!payApp) {
    return <ErrorState message="Pay application not found" />;
  }

  const isEditable = payApp.status === "draft" || payApp.status === "returned_for_revision";
  const isSubmitted = payApp.status === "submitted" || payApp.status === "under_review";
  const isPmOrAdmin = session?.user?.role === "admin" || session?.user?.role === "project_manager";
  const canEditThisPeriod = isEditable || (isSubmitted && isPmOrAdmin);
  const lineItems = payApp.lineItems ?? [];

  // G702 values from enriched API response
  const retPct = payApp.retentionPercent;

  function getEditedValue(itemId: string, field: keyof EditedValues): string | undefined {
    const edited = editedItems[itemId];
    return edited ? edited[field] : undefined;
  }

  function setEditedValue(itemId: string, field: keyof EditedValues, value: string) {
    const original = lineItems.find((l) => l.id === itemId);
    setEditedItems((prev) => ({
      ...prev,
      [itemId]: {
        completedThisPeriod:
          prev[itemId]?.completedThisPeriod ?? String(original?.completedThisPeriod ?? 0),
        storedMaterials: prev[itemId]?.storedMaterials ?? String(original?.storedMaterials ?? 0),
        [field]: value,
      },
    }));
  }

  function computeEditedLine(item: EnrichedSubLineItem) {
    const editedCompleted = getEditedValue(item.id, "completedThisPeriod");
    const editedStored = getEditedValue(item.id, "storedMaterials");
    const completedThisPeriod =
      editedCompleted !== undefined ? Number(editedCompleted) || 0 : item.completedThisPeriod;
    const storedMaterials =
      editedStored !== undefined ? Number(editedStored) || 0 : item.storedMaterials;
    const totalCompletedStored = item.prevApplications + completedThisPeriod + storedMaterials;
    const pct = item.scheduledValue > 0 ? (totalCompletedStored / item.scheduledValue) * 100 : 0;
    const balanceToFinish = item.scheduledValue - totalCompletedStored;
    const retainage = totalCompletedStored * (retPct / 100);

    return {
      completedThisPeriod,
      storedMaterials,
      totalCompletedStored,
      pct,
      balanceToFinish,
      retainage,
    };
  }

  function handleSave() {
    const updates = Object.entries(editedItems).map(([itemId, values]) => {
      const original = lineItems.find((l) => l.id === itemId);
      return {
        commitmentLineItemId: original?.commitmentLineItemId ?? itemId,
        workCompletedThisPeriod: values.completedThisPeriod || "0",
        storedMaterials: values.storedMaterials || "0",
      };
    });
    if (updates.length === 0) {
      toast.error("No changes to save");
      return;
    }
    updateLineItemsMutation.mutate({ lineItems: updates }, { onSuccess: () => setEditedItems({}) });
  }

  function handleSubmit() {
    submitMutation.mutate(payAppId);
  }

  function handleApprove() {
    approveMutation.mutate(payAppId);
  }

  function handleReject() {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    rejectMutation.mutate(
      { id: payAppId, reason: rejectReason },
      {
        onSuccess: () => {
          setRejectDialogOpen(false);
          setRejectReason("");
        },
      },
    );
  }

  function handleDelete() {
    deleteMutation.mutate(payAppId, {
      onSuccess: () => {
        router.navigate({ to: "/pay-applications/subcontract" });
      },
    });
  }

  function handleLienWaiverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadLienWaiverMutation.mutate(file);
    e.target.value = "";
  }

  const hasEdits = Object.keys(editedItems).length > 0;
  const lienWaiverList = lienWaivers ?? [];
  const lienWaiverRequired = payApp.payAppNumber > 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/pay-applications/subcontract">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Pay Application #{payApp.payAppNumber ?? "--"}</h1>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={statusVariant(payApp.status)}>{formatStatusLabel(payApp.status)}</Badge>
            <span className="text-sm text-muted-foreground">
              Period to {formatDate(payApp.billingPeriodEnd)}
            </span>
            {payApp.vendor ? (
              <span className="text-sm text-muted-foreground">| {payApp.vendor}</span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          {isEditable ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash className="mr-2 h-4 w-4" />
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          ) : null}
          {canEditThisPeriod && hasEdits ? (
            <Button onClick={handleSave} disabled={updateLineItemsMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {updateLineItemsMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          ) : null}
          {isEditable ? (
            <Button onClick={handleSubmit} disabled={submitMutation.isPending || hasEdits}>
              <Send className="mr-2 h-4 w-4" />
              Submit for Review
            </Button>
          ) : null}
          {isSubmitted && isPmOrAdmin ? (
            <>
              <Button
                variant="outline"
                onClick={() => returnMutation.mutate(payAppId)}
                disabled={returnMutation.isPending || hasEdits}
              >
                {returnMutation.isPending ? "Returning..." : "Return for Revision"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(true)}
                disabled={rejectMutation.isPending || hasEdits}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button onClick={handleApprove} disabled={approveMutation.isPending || hasEdits}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {/* G702 Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            G702 - Application and Certificate for Payment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between border-b py-1.5">
              <span>1. Original Contract Sum</span>
              <span className="font-medium">{formatCurrency(payApp.originalContractSum)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>2. Net Change by Change Orders</span>
              <span className="font-medium">{formatCurrency(payApp.approvedChangeOrders)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>3. Contract Sum to Date (1 + 2)</span>
              <span className="font-medium">{formatCurrency(payApp.contractSumToDate)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>4. Total Completed &amp; Stored to Date</span>
              <span className="font-medium">{formatCurrency(payApp.totalCompletedStored)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>5. Retainage ({retPct}%)</span>
              <span className="font-medium">{formatCurrency(payApp.retainageAmount)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>6. Total Earned Less Retainage (4 - 5)</span>
              <span className="font-medium">{formatCurrency(payApp.totalEarnedLessRetainage)}</span>
            </div>
            <div className="flex justify-between border-b py-1.5">
              <span>7. Less Previous Certificates</span>
              <span className="font-medium">{formatCurrency(payApp.lessPreviousCertificates)}</span>
            </div>
            <div className="flex justify-between border-b-2 border-foreground py-2 text-base font-bold">
              <span>8. Current Payment Due (6 - 7)</span>
              <span>{formatCurrency(payApp.currentPaymentDue)}</span>
            </div>
            <div className="flex justify-between py-1.5">
              <span>9. Balance to Finish Including Retainage</span>
              <span className="font-medium">
                {formatCurrency(payApp.balanceToFinishIncRetainage)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* G703 Line Items Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">G703 - Continuation Sheet</CardTitle>
        </CardHeader>
        {lineItems.length === 0 ? (
          <CardContent className="py-8 text-center text-muted-foreground">
            No line items found.
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Item</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Scheduled Value</TableHead>
                  <TableHead className="text-right">Previous Apps</TableHead>
                  <TableHead className="text-right">This Period</TableHead>
                  <TableHead className="text-right">Stored</TableHead>
                  <TableHead className="text-right">Total Completed</TableHead>
                  <TableHead className="text-right">%</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Retainage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lineItems.map((item) => {
                  const computed = computeEditedLine(item);
                  const editedCompleted = getEditedValue(item.id, "completedThisPeriod");
                  const editedStored = getEditedValue(item.id, "storedMaterials");

                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.itemNo || "--"}</TableCell>
                      <TableCell>
                        {item.description || "--"}
                        {item.costCodeCode ? (
                          <span className="ml-2 font-mono text-xs text-muted-foreground">
                            [{item.costCodeCode}]
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.scheduledValue)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.prevApplications)}
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditThisPeriod ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="ml-auto w-28 text-right"
                            value={editedCompleted ?? String(item.completedThisPeriod)}
                            onChange={(e) =>
                              setEditedValue(item.id, "completedThisPeriod", e.target.value)
                            }
                          />
                        ) : (
                          formatCurrency(item.completedThisPeriod)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isEditable ? (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            className="ml-auto w-28 text-right"
                            value={editedStored ?? String(item.storedMaterials)}
                            onChange={(e) =>
                              setEditedValue(item.id, "storedMaterials", e.target.value)
                            }
                          />
                        ) : (
                          formatCurrency(item.storedMaterials)
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(computed.totalCompletedStored)}
                      </TableCell>
                      <TableCell className="text-right">{computed.pct.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(computed.balanceToFinish)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(computed.retainage)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Lien Waiver Section */}
      {isEditable ? (
        <Card
          className={
            lienWaiverRequired && lienWaiverList.length === 0
              ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20"
              : ""
          }
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldAlert className="h-4 w-4" />
                Lien Waiver
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => lienWaiverFileRef.current?.click()}
                disabled={uploadLienWaiverMutation.isPending}
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploadLienWaiverMutation.isPending ? "Uploading..." : "Upload Lien Waiver"}
              </Button>
              <input
                ref={lienWaiverFileRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleLienWaiverUpload}
              />
            </div>
          </CardHeader>
          <CardContent>
            {lienWaiverRequired && lienWaiverList.length === 0 ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                A lien waiver is required before submitting — a previous payment has been made on
                this commitment.
              </p>
            ) : null}
            {lienWaiverList.length > 0 ? (
              <div className="space-y-2">
                {lienWaiverList.map((lw) => (
                  <div key={lw.id} className="flex items-center gap-3 rounded-md border p-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="flex-1 text-sm font-medium">{lw.fileName}</span>
                    {lw.uploadedBy ? (
                      <span className="text-xs text-muted-foreground">by {lw.uploadedBy}</span>
                    ) : null}
                    {lw.uploadedDate ? (
                      <span className="text-xs text-muted-foreground">{lw.uploadedDate}</span>
                    ) : null}
                    <a
                      href={`/api/pay-applications/subcontract/${payAppId}/attachments/${lw.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button size="icon" variant="ghost">
                        <Download className="h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                ))}
              </div>
            ) : !lienWaiverRequired ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>No lien waiver uploaded yet</span>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Attachments */}
      <AttachmentSection payAppType="sub" payAppId={payAppId} readOnly={!isEditable} />

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Pay Application</DialogTitle>
            <DialogDescription>
              This pay application will be rejected. The subcontractor will see your reason in their
              portal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectReason.trim()}
            >
              {rejectMutation.isPending ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
